import { useRef } from 'react';

import { AxiosResponse } from 'axios';

import { UseMutationOptions, useMutation } from '@tanstack/react-query';

import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

/**
 * @description useMutation을 래핑하여 중복 호출을 방지하는 커스텀 훅
 */
export const useDedupeMutation = <TParams, TResponse>({
  mutationFn,
  ...options
}: UseMutationOptions<AxiosResponse<TResponse>, unknown, TParams> & {
  mutationFn: (params: TParams) => Promise<AxiosResponse<TResponse>>;
}) => {
  const isFetching = useRef(false);

  const { mutateAsync } = useMutation({
    mutationFn,
    ...options,
    onSettled: (...args) => {
      isFetching.current = false;
      options.onSettled?.(...args);
    },
  });

  const request = async ({
    params,
    onSuccess,
    onError,
    onFinally,
  }: {
    params: TParams;
    onSuccess?: (res: TResponse) => void;
    onError?: (err: TypeErrorFormat) => void;
    onFinally?: () => void;
  }) => {
    if (isFetching.current) {
      return;
    }

    isFetching.current = true;
    requestHelper(() => mutateAsync(params), onSuccess, onError, onFinally);
  };

  return request;
};
